import Vue from 'vue';
import Vuex, { ActionTree, GetterTree, MutationTree } from 'vuex';
import { translations, TranslationsI } from '@/lang/translations';

Vue.use(Vuex);

interface ShopConfigI {
  shopBodyProductBlocks: Record<string, string>;
  numberOfProductsInBlock: number;
}

interface ProductI {
  name: string;
  quantity: number;
  price: number;
}

interface BasketI {
  [productId: number]: ProductI[] | {};
}

interface UserI {
  uid: string;
  username: string;
  token: string;
}

interface ProductI {
  id: number;
  name: string;
  stock: number;
  active: boolean;
  short_desc: string;
  description: string;
  preorder: boolean;
  preorder_end_date: string;
  additional_info: string;
  pegi_id: number;
  created_at: string;
  updated_at: string;
}

interface StateI {
  user: UserI | null;
  language: string;
  text: TranslationsI;
  baners: [];
  config: ShopConfigI | null;
  showMobileMenu: boolean;
  basket: BasketI;
  basketQuantity: number;
  basketTotalPrice: number;
  snackbar: {
    type: string;
    message: string;
  } | null;
  orderData: {
    products: {} | null;
    delivery: number | null;
    address: {} | null;
    payment: number | null;
    totalAmount: number | null;
    acceptedRulePrzelewy24: boolean;
  };
  productsList: ProductI[] | null;
}

const getBasket = () => {
  let basket = {};
  if (localStorage.getItem(btoa('basket')) !== null) {
    const encodedBasket = localStorage.getItem(btoa('basket'));
    basket = encodedBasket !== null ? JSON.parse(encodedBasket) : {};
  }
  return basket;
};

const getUser = () => {
  let user = null;
  if (sessionStorage.getItem(btoa('user')) !== null) {
    const encodedUser = atob(sessionStorage.getItem(btoa('user'))!);
    user = encodedUser !== null ? JSON.parse(encodedUser) : null;
  }
  return user;
};

const basketQuantity = () => {
  let quantity = 0;
  if (Object.keys(getBasket()).length > 0) {
    Object.values(getBasket()).forEach((product: any) => {
      quantity += product.quantity;
    });
  }
  return quantity;
};

const basketTotalPrice = () => {
  let price = 0;
  if (Object.keys(getBasket()).length > 0) {
    Object.values(getBasket()).forEach((product: any) => {
      price += (product.price * product.quantity);
    });
  }
  return price;
};

export const actions: ActionTree<StateI, StateI> = {
  showSnackBar({ commit }, snackbar) {
    commit('showSnackbar', snackbar);
    setTimeout(() => {
      commit('resetSnackbar');
    }, 3000);
  },
};

export const mutations: MutationTree<StateI> = {
  changeLanguage: (state, lang: string) => {
    state.language = lang;
  },
  updateBaners: (state, baners: []) => {
    state.baners = baners;
  },
  setConfig: (state, config: ShopConfigI) => {
    state.config = config;
  },
  setShowMobileMenu: (state, showMenu: false) => {
    Vue.set(state, 'showMobileMenu', showMenu);
  },
  updateBasket: (state, basket) => {
    Vue.set(state, 'basket', basket);
    let quantity = 0;
    let totalPrice = 0;
    Object.values(basket).forEach((product: any) => {
      quantity += product.quantity;
      totalPrice += (product.price * product.quantity);
    });
    Vue.set(state, 'basketQuantity', quantity);
    Vue.set(state, 'basketTotalPrice', totalPrice);
  },
  loginUser: (state, user) => {
    sessionStorage.setItem(btoa('user'), btoa(JSON.stringify(user)));
    Vue.set(state, 'user', user);
  },
  showSnackbar: (state, snackbar) => {
    Vue.set(state, 'snackbar', {
      type: snackbar.type,
      message: snackbar.message,
    });
  },
  resetSnackbar: (state) => {
    Vue.set(state, 'snackbar', null);
  },
  updateOrderData: (state, payload) => {
    const tempOrder = Object.assign(state.orderData, payload);
    Vue.set(state, 'orderData', tempOrder);
  },
  updateProductsList: (state, payload) => {
    Vue.set(state, 'productsList', payload);
  },
};

export const getters: GetterTree<StateI, StateI> = {
  getBasket: (state) => state.basket,
};

export const state: StateI = {
  user: getUser(),
  language: 'pl',
  text: translations.pl,
  baners: [],
  config: null,
  showMobileMenu: false,
  basket: getBasket(),
  basketQuantity: basketQuantity(),
  basketTotalPrice: basketTotalPrice(),
  snackbar: null,
  orderData: {
    products: null,
    delivery: null,
    address: null,
    payment: null,
    totalAmount: null,
    acceptedRulePrzelewy24: false,
  },
  productsList: null,
};

export default new Vuex.Store<StateI>({
  state,
  mutations,
  actions,
  modules: {},
  getters,
});
